import type { MeResponse } from '@onflow/shared';
import { create } from 'zustand';

/**
 * Session store — single source of truth for access token + hydrated identity.
 * Persistence is intentionally in-memory only: the refresh token lives in an
 * httpOnly cookie (not readable from JS) and identity is rehydrated on boot
 * via /v1/auth/me, so refreshing the page always reissues from the server.
 */
interface AuthState {
  accessToken: string | null;
  accessTokenExpiresAt: string | null;
  /** `unknown` = still bootstrapping — routes should show a spinner, not redirect. */
  status: 'unknown' | 'anonymous' | 'authenticated';

  user: MeResponse['user'] | null;
  profile: MeResponse['profile'] | null;

  setSession(token: string, expiresAt: string): void;
  setIdentity(me: MeResponse): void;
  clear(): void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  accessTokenExpiresAt: null,
  status: 'unknown',
  user: null,
  profile: null,

  setSession(token, expiresAt) {
    set({ accessToken: token, accessTokenExpiresAt: expiresAt, status: 'authenticated' });
  },
  setIdentity(me) {
    set({ user: me.user, profile: me.profile });
  },
  clear() {
    set({
      accessToken: null,
      accessTokenExpiresAt: null,
      status: 'anonymous',
      user: null,
      profile: null,
    });
  },
}));
